<template>
  <div class="main-content">
    <div class="report-list">
      <div
        v-for="(item, itemKey) in dataReportConfig"
        :key="itemKey"
        class="report-item"
      >
        <!-- 报告标题栏（可点击展开/收起） -->
        <div
          class="report-header"
          :class="{ 'report-expanded': isItemExpanded(itemKey) }"
          @click="toggleItem(itemKey)"
        >
          <div class="header-left">
            <div class="header-arrow">
              <img
                :src="
                  isItemExpanded(itemKey)
                    ? require('@/assets/images/chatpreview/arrow-down.svg')
                    : require('@/assets/images/chatpreview/arrow-right.svg')
                "
                alt="arrow"
              />
            </div>
            <div class="header-label">{{ item.label }}</div>
          </div>
          <!-- 下载图标（仅在 charts 时显示） -->
          <div
            v-if="itemKey === 'charts'"
            class="header-download"
            @click.stop="downloadReport"
          >
            <img
              src="@/assets/images/chatpreview/data_report_download.png"
              alt="download"
            />
          </div>
        </div>

        <!-- 报告内容区域 -->
        <div class="report-content" v-show="isItemExpanded(itemKey)">
          <!-- charts: 图表展示区 -->
          <div v-if="itemKey === 'charts'" class="charts-container">
            <!-- 上侧圆环图 -->
            <div class="chart-wrapper">
              <div
                :ref="
                  (el) => {
                    if (itemKey === 'charts') pieChartRef = el;
                  }
                "
                class="chart"
              ></div>
            </div>
            <!-- 下侧柱状图 -->
            <div class="chart-wrapper">
              <div
                :ref="
                  (el) => {
                    if (itemKey === 'charts') barChartRef = el;
                  }
                "
                class="chart"
              ></div>
            </div>
          </div>

          <!-- analysis: 总结分析文本 -->
          <div v-if="itemKey === 'analysis'" class="summary-content">
            <div class="summary-text" v-html="formatMessage(item.value)"></div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import * as echarts from "echarts";
import MarkdownIt from "markdown-it";
// import hljs from "highlight.js";
// import "highlight.js/styles/atom-one-dark.css";
import katex from "katex";
import mkatex from "markdown-it-katex";
import "katex/dist/katex.min.css";

// 配置 markdown-it
const md = new MarkdownIt({
  html: true,
  linkify: true,
  typographer: true,
  breaks: true,
});

md.use(mkatex);

export default {
  name: "DataReport",
  props: {
    previewData: {
      type: Object,
      default: () => null,
    },
  },
  data() {
    return {
      expandedItems: [],
      barChartInstance: null,
      pieChartInstance: null,
      barChartRef: null,
      pieChartRef: null,
    };
  },
  computed: {
    dataReportConfig() {
      // 如果 previewData 存在，优先使用预览数据，否则使用配置数据
      if (this.previewData) {
        return this.previewData;
      }
      return {};
    },
  },
  methods: {
    // 判断项目是否展开
    isItemExpanded(itemKey) {
      return this.expandedItems.includes(itemKey);
    },
    // 切换项目展开/收起
    toggleItem(itemKey) {
      const index = this.expandedItems.indexOf(itemKey);
      if (index > -1) {
        this.expandedItems.splice(index, 1);
        // 如果是图表项，销毁图表实例
        if (itemKey === "charts") {
          this.destroyCharts();
        }
      } else {
        this.expandedItems.push(itemKey);
        // 如果是图表项，初始化图表
        if (itemKey === "charts") {
          // 等待 DOM 更新和动画完成
          this.$nextTick(() => {
            setTimeout(() => {
              this.initCharts();
            }, 300); // 等待动画完成
          });
        }
      }
    },
    // 初始化图表
    initCharts() {
      const charts = this.dataReportConfig.charts.value;
      if (!charts) {
        console.warn("图表配置不存在");
        return;
      }

      // 确保 DOM 已渲染，使用双重 nextTick 和延迟
      this.$nextTick(() => {
        setTimeout(() => {
          // 初始化圆环图
          const pieChartEl = this.pieChartRef;
          // 确保是有效的 DOM 元素
          if (pieChartEl && charts.pieChart && charts.pieChart.data) {
            // 检查是否是 DOM 元素
            if (!(pieChartEl instanceof HTMLElement)) {
              console.warn(
                "圆环图ref不是有效的DOM元素:",
                pieChartEl
              );
              return;
            }

            // 检查元素是否可见
            try {
              const rect = pieChartEl.getBoundingClientRect();
              if (rect.width === 0 || rect.height === 0) {
                console.warn("圆环图元素不可见, 重试...");
                setTimeout(() => this.initCharts(), 200);
                return;
              }
            } catch (e) {
              console.warn("检查圆环图元素可见性错误:", e);
              return;
            }

            // 如果已存在实例，先销毁
            if (this.pieChartInstance) {
              try {
                this.pieChartInstance.dispose();
              } catch (e) {
                console.warn("销毁圆环图错误:", e);
              }
            }

            try {
              this.pieChartInstance = echarts.init(pieChartEl);
              const pieData = charts.pieChart.data;
              
              // 获取实际的数据数组：如果 pieData 是数组则直接使用，否则使用 pieData.data
              const dataArray = Array.isArray(pieData) ? pieData : (pieData?.data || []);
              
              // 检查数据数组是否存在且不为空
              if (!Array.isArray(dataArray) || dataArray.length === 0) {
                console.warn("圆环图数据不存在或为空", { pieData, dataArray });
                return;
              }

              // 根据数据量动态计算圆环图高度（数据项多时需要更多空间显示标签）
              // 基础高度 220px，每增加一个数据项增加 20px，最大 400px
              // const pieChartHeight = Math.min(220 + dataArray.length * 20, 400);
              // pieChartEl.style.height = `${pieChartHeight}px`;

              const pieOption = {
                title: {
                  text: charts.pieChart.title,
                  subtext: charts.pieChart.subtitle,
                  left: "left",
                  textStyle: {
                    fontSize: 14,
                  },
                  subtextStyle: {
                    fontSize: 12,
                  },
                },
                tooltip: {
                  trigger: "item",
                  formatter: "{b}: {c} kgCO&#8322;e ({d}%)",
                },
                legend: {
                  show: true,
                  right:0,
                  bottom:0,
                  orient:'vertical',
                },
                series: [
                  {
                    type: "pie",
                    radius: ["40%", "70%"],
                    center: ["50%", "60%"],
                    avoidLabelOverlap: true,
                    // itemStyle: {
                    //   borderRadius: 0,
                    //   borderColor: "#fff",
                    //   borderWidth: 2,
                    // },
                    label: {
                      show: false,
                      fontSize: 12,
                      color: "#000",
                    },
                    // emphasis: {
                    //   label: {
                    //     show: true,
                    //     fontSize: 14,
                    //     fontWeight: "bold",
                    //   },
                    // },
                    data: dataArray.map((item, index) => {
                      // 根据索引设置颜色
                      const colors = ["rgba(17, 78, 147, 1)", "rgba(28, 130, 245, 1)", "rgba(119, 180, 249, 1)", "rgba(210, 230, 253, 1)"];
                      return {
                        name: item.name,
                        value: item.value,
                        itemStyle: {
                          color: colors[index % colors.length],
                        },
                      };
                    }),
                  },
                ],
              };
              this.pieChartInstance.setOption(pieOption);
              
              // 设置高度后需要重新调整图表大小
              this.$nextTick(() => {
                if (this.pieChartInstance && !this.pieChartInstance.isDisposed()) {
                  this.pieChartInstance.resize();
                }
              });

              // 监听窗口大小变化
              window.addEventListener("resize", this.handlePieChartResize);
            } catch (e) {
              console.error("初始化圆环图错误:", e);
            }
          } else {
            console.warn("圆环图ref或数据不存在", {
              ref: pieChartEl,
              data: charts.pieChart.data,
            });
          }

          // 初始化柱状图
          const barChartEl = this.barChartRef;
          // 确保是有效的 DOM 元素
          if (barChartEl && charts.barChart && charts.barChart.data) {
            // 如果已存在实例，先销毁
            if (this.barChartInstance) {
              try {
                this.barChartInstance.dispose();
              } catch (e) {
                console.warn("销毁柱状图错误:", e);
              }
            }

            try {
              this.barChartInstance = echarts.init(barChartEl);
              
              // 获取柱状图数据数组
              const barData = charts.barChart.data;
              const barDataArray = Array.isArray(barData) ? barData : (barData?.data || []);
              
              // 检查数据数组是否存在且不为空
              if (!Array.isArray(barDataArray) || barDataArray.length === 0) {
                console.warn("柱状图数据不存在或为空", { barData, barDataArray });
                return;
              }
              
              // 根据数据量动态计算柱状图高度（横向柱状图，每个数据项需要约 30px 高度）
              // 基础高度 220px，每个数据项 30px，最小 220px
              const baseHeight = 0; // 基础高度（包含标题、间距等）
              const itemHeight = 30; // 每个数据项的高度
              const calculatedHeight = baseHeight + (barDataArray.length * itemHeight);
              // 设置最小高度 220px，最大高度 500px（避免过高）
              const barChartHeight = Math.max(220, Math.min(calculatedHeight, 600));
              barChartEl.style.height = `${barChartHeight}px`;
              
              const barOption = {
                title: {
                  text: charts.barChart.title,
                  subtext: charts.barChart.subtitle,
                  left: "left",
                  textStyle: {
                    fontSize: 14,
                  },
                  subtextStyle: {
                    fontSize: 12,
                  },
                },
                tooltip: {
                  trigger: "axis",
                  axisPointer: {
                    type: "shadow",
                  },
                  formatter: "{b}: {c} kgCO&#8322;e",
                },
                grid: {
                  left: "5%",
                  right: "5%",
                  bottom: "2%",
                  containLabel: true,
                },
                legend: {
                },
                xAxis: {
                  type: "value",
                  axisLine: {
                    show: false,
                  },
                  axisTick: {
                    show: false,
                  },
                  axisLabel: {
                    show: false,
                  },
                  splitLine: {
                    show: false,
                  },
                },
                yAxis: {
                  type: "category",
                  data: barDataArray.map((item) => item.name),
                  axisLine: {
                    show: false,
                  },
                  axisTick: {
                    show: false,
                  },
                  axisLabel: {
                    color: "#394342",
                    fontSize: 12,
                  },
                },
                series: [
                  {
                    type: "bar",
                    data: barDataArray.map((item) => item.value),
                    itemStyle: {
                      color: "rgba(115, 155, 227, 1)",
                    },
                  },
                ],
              };
              this.barChartInstance.setOption(barOption);
              
              // 设置高度后需要重新调整图表大小
              this.$nextTick(() => {
                if (this.barChartInstance && !this.barChartInstance.isDisposed()) {
                  this.barChartInstance.resize();
                }
              });

              // 监听窗口大小变化
              window.addEventListener("resize", this.handleBarChartResize);
            } catch (e) {
              console.error("初始化柱状图错误:", e);
            }
          } else {
            console.warn("柱状图ref或数据不存在", {
              ref: barChartEl,
              data: charts.barChart.data,
            });
          }
        }, 100); // 延迟 100ms 确保 DOM 完全渲染
      });
    },
    // 销毁图表
    destroyCharts() {
      if (this.barChartInstance) {
        window.removeEventListener("resize", this.handleBarChartResize);
        this.barChartInstance.dispose();
        this.barChartInstance = null;
      }
      if (this.pieChartInstance) {
        window.removeEventListener("resize", this.handlePieChartResize);
        this.pieChartInstance.dispose();
        this.pieChartInstance = null;
      }
      // 清理 ref
      this.barChartRef = null;
      this.pieChartRef = null;
    },
    // 柱状图 resize 处理
    handleBarChartResize() {
      if (this.barChartInstance && !this.barChartInstance.isDisposed()) {
        this.barChartInstance.resize();
      }
    },
    // 圆环图 resize 处理
    handlePieChartResize() {
      if (this.pieChartInstance && !this.pieChartInstance.isDisposed()) {
        this.pieChartInstance.resize();
      }
    },
    // 下载报告
    downloadReport() {
      const charts = this.dataReportConfig.charts;
      if (!charts || !charts.link) {
        this.$message?.warning?.("下载链接不存在");
        console.warn("下载链接不存在", charts);
        return;
      }

      const downloadUrl = charts.link;
      
      // 将下载 URL 存储到 window 对象
      window.open(downloadUrl, "_blank");
    },
    // 格式化消息内容（与 ChatPage.vue 相同的 markdown 渲染规则）
    formatMessage(text) {
      if (!text) return "";

      // 自定义链接渲染规则
      md.renderer.rules.link_open = (tokens, idx, options, env, self) => {
        const token = tokens[idx];
        const hrefIndex = token.attrIndex("href");
        if (hrefIndex >= 0) {
          const href = token.attrs[hrefIndex][1];
          const hasTarget = this.checkUrlContainsPath(href, 'ai_open_file');
          if (hasTarget) {
            //假设fileId是会话id，用于后期传参
            const fileId = href.split('/').pop();
            const buttonHtml = `<span class="span-link" data-file-id="${fileId}" onclick="window.openExcelViewer('${fileId}')">${tokens[idx + 1].content}</span>`;

            // 2. 标记当前链接为特殊链接
            token.attrSet('is-special-link', 'true');

            // 3. 清空后续文本token内容（原始URL）
            if (tokens[idx + 1] && tokens[idx + 1].type === 'text') {
              tokens[idx + 1].content = ''; // 关键：清除URL文本
            }

            return buttonHtml;
          } else {
            token.attrSet("target", "_blank");
            token.attrSet("rel", "noopener noreferrer");
          }
        }
        return self.renderToken(tokens, idx, options);
      };

      md.renderer.rules.link_close = (tokens, idx, options, env, self) => {
        // 获取对应打开标签token（link_close在link_open后2个位置）
        const linkOpenToken = tokens[idx - 2];
        if (linkOpenToken && linkOpenToken.attrGet('is-special-link') === 'true') {
          return ''; // 特殊链接不渲染闭合标签
        }
        return self.renderToken(tokens, idx, options); // 普通链接正常渲染
      };

      // 自定义数学公式渲染规则
      const renderMath = (content, displayMode) => {
        try {
          return katex.renderToString(content, { displayMode });
        } catch (error) {
          return displayMode ? `$$${content}$$` : `$${content}$`;
        }
      };

      // 自定义数学公式渲染规则-行内公式
      md.renderer.rules.math_inline = function (tokens, idx) {
        const content = tokens[idx].content;
        try {
          return katex.renderToString(content, { displayMode: false });
        } catch (error) {
          return `$${content}$`;
        }
      };

      // 自定义数学公式渲染规则-块级
      md.renderer.rules.math_block = function (tokens, idx) {
        const content = tokens[idx].content;
        try {
          return katex.renderToString(content, { displayMode: true });
        } catch (error) {
          return `$$${content}$$`;
        }
      };

      // 渲染数学公式
      text = text.replace(/\\\[/g, "$$").replace(/\\\]/g, "$$"); // 替换 \[ 和 \] 为 $$
      text = text.replace(/\\\(/g, '$').replace(/\\\)/g, '$') // 替换 \( 和 \) 为 $
      text = text
        .replace(/\$\$([\s\S]*?)\$\$/g, (_, formula) =>
          renderMath(formula, true)
        )
        .replace(/\$([^$]*?)\$/g, (_, formula) => renderMath(formula, false));

      // 如果\n后面紧跟数字, 转换为 \n 1
      text = text.replace(/\n(\d+)/g, "\n $1 ");

      return md.render(text);
    },
    // 检查URL是否包含指定路径
    checkUrlContainsPath(url, targetPath) {
      if (!url) return false;
      try {
        const urlObj = new URL(url);
        const pathname = urlObj.pathname;
        const pathSegments = pathname.split('/').filter(segment => segment.trim() !== '');
        return pathSegments.includes(targetPath);
      } catch (err) {
        console.error('URL解析错误:', err);
        return false;
      }
    },
  },
  mounted() {
    // 如果默认展开，初始化图表
    if (this.expandedItems.includes("charts")) {
      // 使用双重 nextTick 确保 DOM 完全渲染
      this.$nextTick(() => {
        this.$nextTick(() => {
          this.initCharts();
        });
      });
    }
  },
  beforeDestroy() {
    // 清理图表实例
    this.destroyCharts();
  },
};
</script>

<style scoped lang="scss">
.main-content {
  width: 100%;
  height: 100%;
}

.report-list {
  width: 100%;
  display: flex;
  flex-direction: column;
  gap: 8px;
  padding: 12px 16px;
}

.report-item {
  width: 100%;
}

.report-header {
  width: 100%;
  height: 40px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  cursor: pointer;
  transition: all 0.3s ease;
  border-radius: 8px;
  padding: 12px 16px;
  border-radius: 8px;

  &:hover {
    background: rgba(243, 247, 250, 1);
  }
}

.header-left {
  display: flex;
  align-items: center;
  flex: 1;
}

.header-arrow {
  width: 12px;
  height: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-right: 10px;
  flex-shrink: 0;
  transition: transform 0.3s ease;

  img {
    width: 100%;
    height: 100%;
    object-fit: contain;
  }
}

.header-label {
  flex: 1;
  font-size: 14px;
  font-weight: 700;
  line-height: 24px;
  color: rgba(5, 32, 49, 1);

  .report-header:not(.report-expanded) & {
    color: rgba(170, 179, 187, 1);
  }
}

.header-download {
  width: 20px;
  height: 20px;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: opacity 0.3s ease;
  flex-shrink: 0;
  margin-left: 12px;
  opacity: 0.5;

  &:hover {
    opacity: 1;
  }

  img {
    width: 100%;
    height: 100%;
    object-fit: contain;
  }
}

.report-header.report-expanded {
  background: rgba(219, 232, 245, 1);
  border-radius: 8px;
}

.report-content {
  width: 100%;
  padding: 12px;
  animation: slideDown 0.3s ease;
}

.charts-container {
  width: 100%;
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.chart-wrapper {
  background: rgba(244, 248, 252, 1);
  border-radius: 8px;
  padding: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
}

.chart {
  width: 100%;
  min-height: 220px;
  display: block;
  // 高度由 JavaScript 根据数据量动态设置，不再限制最大高度
}

.summary-content{
  padding: 12px;
  background: rgba(243, 247, 250, 1);
  border-radius: 8px;
}

.summary-text {
  font-size: 14px;
  line-height: 22px;
  color: rgba(57, 67, 66, 1);
  text-align: justify;
  text-justify: inter-word;
  word-break: break-all;
  // 首行缩进2个字符
  text-indent: 2em;

  // Markdown 基础样式
  :deep(h1) {
    font-size: 20px;
    font-weight: 600;
    margin: 16px 0 12px;
    color: #1a73e8;
  }

  :deep(h2) {
    font-size: 18px;
    font-weight: 600;
    margin: 14px 0 10px;
    color: #1a73e8;
  }

  :deep(h3) {
    font-size: 16px;
    font-weight: 600;
    margin: 12px 0 8px;
    color: #1a73e8;
  }

  :deep(p) {
    margin: 8px 0;
    line-height: 1.6;
  }

  // :deep(ul),
  // :deep(ol) {
  //   margin: 8px 0;
  //   padding-left: 24px;
  // }

  // :deep(li) {
  //   margin: 4px 0;
  //   line-height: 1.5;
  // }

  :deep(strong) {
    font-weight: 600;
    color: #1a73e8;
  }

  :deep(em) {
    font-style: italic;
  }

  :deep(a) {
    color: #1a73e8;
    text-decoration: underline;
  }

  :deep(a:hover) {
    text-decoration: underline;
  }

  :deep(blockquote) {
    margin: 16px 0;
    padding: 0 16px;
    color: #6a737d;
    border-left: 4px solid #dfe2e5;
  }

  :deep(hr) {
    height: 1px;
    margin: 16px 0;
    background-color: #eaecef;
    border: none;
  }

  // 特殊链接样式
  :deep(.span-link) {
    color: #1a73e8;
    cursor: pointer;
    text-decoration: underline;
  }
}

@keyframes slideDown {
  from {
    opacity: 0;
    max-height: 0;
  }
  to {
    opacity: 1;
    max-height: 1000px;
  }
}
</style>
